/** Real client for `/api/v1/ai/menu-round` (api/app/ai/menu_round_routes.py). Same origin + session cookie as the chat API. */
import { ApiError } from '../client'
import { reportUnauthorized } from '../http'
import type { EditBody, MenuAction, MenuAlert, MenuRoundApi, MenuRoundState, NewRoundBody } from './menuTypes'

export class HttpMenuRoundApi implements MenuRoundApi {
  constructor(
    private readonly base: string,
    /** the server may fetch the POS catalog (≤ 10 s) */
    private readonly timeoutMs = 20000,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  private url(path: string): string {
    return new URL(`${this.base.replace(/\/+$/, '')}/api/v1/ai/menu-round${path}`, window.location.origin).toString()
  }

  private async req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), this.timeoutMs)
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    try {
      const res = await this.fetchImpl(this.url(path), {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        credentials: 'same-origin',
        signal: ctrl.signal,
      })
      if (res.status === 401) reportUnauthorized()
      if (!res.ok) {
        let detail = res.statusText
        try {
          const j = (await res.json()) as { detail?: unknown }
          if (typeof j.detail === 'string') detail = j.detail
        } catch {
          /* body not JSON */
        }
        throw new ApiError(res.status, detail || `HTTP ${res.status}`)
      }
      return (await res.json()) as T
    } catch (e) {
      if (e instanceof ApiError) throw e
      if (e instanceof DOMException && e.name === 'AbortError') throw new ApiError(0, 'หมดเวลารอเซิร์ฟเวอร์')
      throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้')
    } finally {
      clearTimeout(timer)
    }
  }

  load(opts: { fresh?: boolean } = {}): Promise<MenuRoundState> {
    return this.req('GET', opts.fresh ? '?fresh=1' : '')
  }

  create(body: NewRoundBody): Promise<MenuRoundState> {
    return this.req('POST', '', body)
  }

  edit(id: string, body: EditBody): Promise<MenuRoundState> {
    return this.req('PATCH', `/${encodeURIComponent(id)}`, body)
  }

  act(id: string, action: MenuAction): Promise<MenuRoundState> {
    return this.req('POST', `/${encodeURIComponent(id)}/${action}`)
  }

  alert(): Promise<MenuAlert | null> {
    return this.req('GET', '/alert')
  }
}
